import React, { useState, useEffect } from 'react';
import { Badge, Button, Col, Form, Image, InputGroup, Modal, Row, Table, Nav, Space, Input, Select, Card, Alert } from "react-bootstrap";
import { Dropdown, ButtonGroup } from "react-bootstrap";
import { message } from "antd";
import { oneKlicks } from "../../../SDK/oneclick";
import ImageShow from '../../../Uitiles/ImageShow';
import { OneKlicksFileSDK } from '../../../SDK/OneKlicksFileSDK';
import { toast } from 'react-toastify';
import formatDoctorName from '../../../Uitiles/FormatName';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import './InvoiceTemplate.css';
import { getSettings } from '../../../services/settingsService';

const InvoiceTemplate = ({
    invoice,
    invoice_items,
    showActions = true,
    showPreview = true,
    onDownload,
    onEmail,
    onView,
    onDelete
}) => {
    const [showPDFPreview, setShowPDFPreview] = useState(false);
    const [companyInfo, setCompanyInfo] = useState({});

    useEffect(() => {
        getSettings().then(settings => {
            setCompanyInfo(settings);
        });
    }, []);

    const getStatusBadge = (status) => {
        switch (status) {
            case 'Paid':
                return <Badge bg="success">Paid</Badge>;
            case 'Pending':
                return <Badge bg="warning">Pending</Badge>;
            case 'Overdue':
                return <Badge bg="danger">Overdue</Badge>;
            case 'Draft':
                return <Badge bg="secondary">Draft</Badge>;
            default:
                return <Badge bg="info">{status}</Badge>;
        }
    };

    const generatePDF = (invoiceData) => {
        // A4 dimensions: 210mm x 297mm
        const doc = new jsPDF('p', 'mm', 'a4');

        // Colors
        const primaryColor = [41, 128, 185]; // Blue
        const secondaryColor = [52, 73, 94]; // Dark gray
        const accentColor = [46, 204, 113]; // Green

        // A4 margins
        const margin = 15; // 15mm margins
        const pageWidth = 210;
        const pageHeight = 297;
        const contentWidth = pageWidth - (margin * 2);

        // Header
        doc.setFillColor(...primaryColor);
        doc.rect(0, 0, pageWidth, 25, 'F');

        // Company Logo/Name
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(18);
        doc.setFont('helvetica', 'bold');
        doc.text(companyInfo.name, margin, 18);

        // Invoice Title
        doc.setTextColor(...secondaryColor);
        doc.setFontSize(14);
        doc.setFont('helvetica', 'bold');
        doc.text('INVOICE', pageWidth - margin - 30, 18);

        // Invoice Number and Date
        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        doc.text(`Invoice #: ${invoiceData.id}`, pageWidth - margin - 30, 25);
        doc.text(`Date: ${invoiceData.createdDate}`, pageWidth - margin - 30, 30);
        doc.text(`Due Date: ${invoiceData.dueDate}`, pageWidth - margin - 30, 35);

        // Status Badge
        const statusColor = invoiceData.status === 'Paid' ? accentColor :
            invoiceData.status === 'Pending' ? [241, 196, 15] :
                invoiceData.status === 'Overdue' ? [231, 76, 60] : secondaryColor;

        doc.setFillColor(...statusColor);
        doc.roundedRect(pageWidth - margin - 25, 40, 20, 6, 1, 1, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(7);
        doc.setFont('helvetica', 'bold');
        doc.text(invoiceData.status.toUpperCase(), pageWidth - margin - 22, 44);

        // Company Details
        doc.setTextColor(...secondaryColor);
        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        doc.text('From:', margin, 45);
        doc.setFont('helvetica', 'bold');
        doc.text(companyInfo.name, margin, 50);
        doc.setFont('helvetica', 'normal');
        doc.text(companyInfo.address, margin, 55);
        doc.text(companyInfo.city, margin, 60);
        doc.text(companyInfo.country, margin, 65);
        doc.text(`Phone: ${companyInfo.phone}`, margin, 75);
        doc.text(`Email: ${companyInfo.email}`, margin, 80);

        // Client Details
        doc.text('Bill To:', margin, 90);
        doc.setFont('helvetica', 'bold');
        doc.text(invoiceData.client.name, margin, 95);
        doc.setFont('helvetica', 'normal');
        doc.text(invoiceData.client.email, margin, 100);

        // Line separator
        doc.setDrawColor(...primaryColor);
        doc.setLineWidth(0.3);
        doc.line(margin, 105, pageWidth - margin, 105);

        // Service Details Table
        const tableData = [
            ['Description', 'Package', 'Amount'],
            [invoiceData.description, invoiceData.package, `₹${invoiceData.amount.toFixed(2)}`]
        ];

        doc.autoTable({
            startY: 110,
            head: [tableData[0]],
            body: [tableData[1]],
            theme: 'grid',
            headStyles: {
                fillColor: primaryColor,
                textColor: [255, 255, 255],
                fontStyle: 'bold',
                fontSize: 9
            },
            bodyStyles: {
                textColor: secondaryColor,
                fontSize: 8
            },
            columnStyles: {
                0: { cellWidth: contentWidth * 0.6 },
                1: { cellWidth: contentWidth * 0.25 },
                2: { cellWidth: contentWidth * 0.15, halign: 'right' }
            },
            margin: { left: margin, right: margin },
            tableWidth: contentWidth
        });

        // Total Amount
        const finalY = doc.lastAutoTable.finalY + 15;
        doc.setFontSize(11);
        doc.setFont('helvetica', 'bold');
        doc.text('Total Amount:', pageWidth - margin - 60, finalY);
        doc.text(`₹${invoiceData.amount.toFixed(2)}`, pageWidth - margin - 10, finalY);

        // Payment Information
        doc.setFontSize(8);
        doc.setFont('helvetica', 'normal');
        doc.text('Payment Terms:', margin, finalY + 20);
        doc.text('• Payment due within 30 days of invoice date', margin, finalY + 25);
        doc.text('• Late payments may incur additional charges', margin, finalY + 30);
        doc.text(`• For questions about this invoice, contact ${companyInfo.email}`, margin, finalY + 35);

        // Footer
        doc.setFillColor(...secondaryColor);
        doc.rect(0, pageHeight - 15, pageWidth, 15, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(7);
        doc.text(`Thank you for choosing ${companyInfo.name}!`, margin, pageHeight - 8);
        doc.text('This is a computer-generated invoice.', margin, pageHeight - 3);

        // Save the PDF
        doc.save(`Invoice_${invoiceData.id}_${invoiceData.client.name.replace(/\s+/g, '_')}.pdf`);
    };

    const handleDownload = () => {
        if (onDownload) {
            onDownload(invoice);
        } else {
            generatePDF(invoice);
        }
        toast.success(`Downloading invoice #${invoice.id}`);
    };

    const handleEmail = () => {
        if (onEmail) {
            onEmail(invoice);
        } else {
            toast.info('Email functionality not implemented');
        }
    };

    const handleView = () => {
        if (onView) {
            onView(invoice);
        }
    };

    const handleDelete = () => {
        if (onDelete) {
            onDelete(invoice.id);
        }
    };

    if (!invoice) {
        return (
            <div className="text-center p-4">
                <Alert variant="info">No invoice data provided</Alert>
            </div>
        );
    }

    return (
        <div className="invoice-template">
            {/* Invoice Preview Card */}
            <Card className="mb-4">
                <Card.Header className="bg-primary text-white">
                    <div className="d-flex justify-content-between align-items-center">
                        <h5 className="mb-0">Invoice #{invoice.id}</h5>
                        <div className="d-flex gap-2">
                            {getStatusBadge(invoice.status)}
                            {showActions && (
                                <Dropdown>
                                    <Dropdown.Toggle variant="light" size="sm">
                                        <i className="fas fa-ellipsis-v"></i>
                                    </Dropdown.Toggle>
                                    <Dropdown.Menu>
                                        <Dropdown.Item onClick={handleView}>
                                            <i className="fas fa-eye me-2"></i>View Details
                                        </Dropdown.Item>
                                        {showPreview && (
                                            <Dropdown.Item onClick={() => setShowPDFPreview(true)}>
                                                <i className="fas fa-file-pdf me-2"></i>Preview PDF
                                            </Dropdown.Item>
                                        )}
                                        <Dropdown.Item onClick={handleEmail}>
                                            <i className="fas fa-envelope me-2"></i>Send Email
                                        </Dropdown.Item>
                                        <Dropdown.Item onClick={handleDownload}>
                                            <i className="fas fa-download me-2"></i>Download PDF
                                        </Dropdown.Item>
                                        <Dropdown.Item onClick={() => window.print()}>
                                            <i className="fas fa-print me-2"></i>Print
                                        </Dropdown.Item>
                                        <Dropdown.Divider />
                                        <Dropdown.Item onClick={handleDelete} className="text-danger">
                                            <i className="fas fa-trash me-2"></i>Delete
                                        </Dropdown.Item>
                                    </Dropdown.Menu>
                                </Dropdown>
                            )}
                        </div>
                    </div>
                </Card.Header>
                <Card.Body>
                    <div className="d-flex justify-content-between w-100">
                        <div className="w-50">
                            <ImageShow url={companyInfo.logo_url} width={250} />
                        </div>
                        {/* invoice status */}
                        <div className="w-50 align-items-end" style={{ display: 'flex', flexDirection: 'column', alignItems: 'end' }}>
                            <h6 className="text-muted">Invoice Status:</h6>
                            <p className="mb-1">
                                <span 
                                    className={`badge fs-5 px-3 py-2 ${
                                        invoice.status?.toLowerCase() === 'paid' ? 'bg-success' :
                                        invoice.status?.toLowerCase() === 'pending' ? 'bg-warning' :
                                        invoice.status?.toLowerCase() === 'overdue' ? 'bg-danger' :
                                        'bg-secondary'
                                    }`}
                                    style={{ 
                                        fontSize: '1.1rem',
                                        fontWeight: 'bold',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.5px'
                                    }}
                                >
                                    {invoice.status}
                                </span>
                            </p>
                        </div>
                    </div>
                    <hr />
                    <Row>
                        <Col md={6}>

                            <h6 className="text-muted">From:</h6>
                            <p className="mb-1"><strong>{companyInfo.site_name}</strong></p>
                            <p className="mb-1 text-muted small">{companyInfo.contact_address}</p>
                            <p className="mb-1 text-muted small">Phone: {companyInfo.contact_phone}</p>
                            <p className="mb-1 text-muted small">Email: {companyInfo.contact_email}</p>
                            <p className="mb-1 text-muted small">TAX ID: {companyInfo.tax_id}</p>
                            <p className="mb-1 text-muted small">GSTIN: {companyInfo.gstin}</p>
                        </Col>
                        <Col md={6}>

                            <h6 className="text-muted">Bill To:</h6>
                            <p className="mb-1"><strong>{invoice.client.name}</strong></p>
                            <p className="mb-1 text-muted small">{invoice.client.email}</p>
                            <hr />
                            <div className="row">
                                <div className="col-6">
                                    <p className="mb-1 text-muted small">Invoice Date:</p>
                                    <p className="mb-1"><strong>{invoice.createdDate}</strong></p>
                                </div>
                                <div className="col-6">
                                    <p className="mb-1 text-muted small">Due Date:</p>
                                    <p className="mb-1"><strong>{invoice.dueDate}</strong></p>
                                </div>
                            </div>
                        </Col>
                    </Row>

                    <hr />

                    {/* Service Details */}
                    <div className="table-responsive">
                        <Table size="sm" className="mb-0">
                            <thead className="table-primary">
                                <tr>
                                    <th>ID</th>
                                    <th>Description</th>
                                    <th>Package</th>
                                    <th>Quantity</th>
                                    <th>TAX %</th>
                                    <th>TAX Amount</th>
                                    <th className="text-end">Amount</th>
                                    <th className="text-end">Total Amount</th>
                                </tr>
                            </thead>
                            <tbody>
                                {invoice_items.map((item) => (
                                    <tr key={item.id}>
                                        <td>{item.id}</td>
                                        <td>{item.description}</td>
                                        <td>{item.package}</td>
                                        <td>{item.quantity}</td>
                                        <td>{item.tax_percentage}</td>
                                        <td>{item.tax_amount}</td>
                                        <td className="text-end"><strong>₹{(parseFloat(item.amount) || 0).toFixed(2)}</strong></td>
                                        <td className="text-end"><strong>₹{(parseFloat(item.total_amount) || 0).toFixed(2)}</strong></td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </div>

                    <hr />

                    <div className="d-flex justify-content-between">
                        <div className="w-60" style={{ width: '60%' }}>
                            <h6 className="text-muted">Payment Terms:</h6>
                            <p className="mb-1 text-muted small">Payment due within 30 days of invoice date</p>
                            <p className="mb-1 text-muted small">Late payments may incur additional charges</p>
                            <p className="mb-1 text-muted small">For questions about this invoice, contact {companyInfo.email}</p>

                            <div className="justify-content-start">
                                <h6 className="text-muted">Payment Method :</h6>
                                <p className="mb-0 text-muted">Bank Name : Indian Bank</p>
                                <p className="mb-0 text-muted">Bank Account Number : 1234567890</p>
                                <p className="mb-0 text-muted">Bank IFSC Code : INDB0000001</p>
                                <p className="mb-0 text-muted">Bank Account Holder : John Doe</p>
                            </div>
                        </div>
                        <div className="text-end w-40" style={{ width: '40%' }}>
                            <div className="d-flex space-between" style={{ justifyContent: 'space-between' }}>
                                <p className="mb-0">Total: </p>
                                <p className="mb-0">₹{(parseFloat(invoice.amount) || 0).toFixed(2)}</p>
                            </div>
                            <hr style={{ marginBottom: '3px', marginTop: '3px' }} />
                            <div className="d-flex space-between" style={{ justifyContent: 'space-between' }}>
                                <p className="mb-0">CGST: </p>
                                <p className="mb-0">9%</p>
                            </div>
                            <div className="d-flex space-between" style={{ justifyContent: 'space-between' }}>
                                <p className="mb-0">SGST: </p>
                                <p className="mb-0">9%</p>
                            </div>
                            <div className="d-flex space-between" style={{ justifyContent: 'space-between' }}>
                                <p className="mb-0">IGST: </p>
                                <p className="mb-0">0%</p>
                            </div>
                            <hr style={{ marginBottom: '3px', marginTop: '3px' }} />
                            <div className="d-flex space-between" style={{ justifyContent: 'space-between' }}>
                                <p className="mb-0">Tax Amount: </p>
                                <p className="mb-0">₹{(parseFloat(invoice.tax_amount) || 0).toFixed(2)}</p>
                            </div>
                            <hr style={{ marginBottom: '3px', marginTop: '3px' }} />
                            <div className="d-flex space-between" style={{ justifyContent: 'space-between' }}>
                                <p className="mb-0">Discount: </p>
                                <p className="mb-0">10%</p>
                            </div>
                            <div className="d-flex space-between" style={{ justifyContent: 'space-between' }}>
                                <p className="mb-0">Discount Amount: </p>
                                <p className="mb-0">1000</p>
                            </div>
                            <hr style={{ marginBottom: '3px', marginTop: '3px' }} />
                            <div className="d-flex space-between" style={{ justifyContent: 'space-between' }}>
                                <p className="mb-0">Grand Total: </p>
                                <p className="mb-0">₹{(parseFloat(invoice.total_amount) || 0).toFixed(2)}</p>
                            </div>
                        </div>
                    </div>

                    <hr />

                    <div className="d-flex justify-content-between">
                        <div className="align-items-start">
                            <p className="mb-0"> Pay With :</p>
                            <p className="mb-0"> Upi Id : 9490279973@ybl</p>
                        </div>
                        <div className="align-items-end">
                            <ImageShow url={companyInfo.author_signature_url} width={150} />
                            <p className="mb-0 text-muted" style={{ textAlign: 'end' }}>Author Signature</p>
                        </div>
                    </div>

                    <hr />

                    <div className="d-flex justify-content-center" style={{ display: 'flex', flexDirection: 'column', alignContent: 'center', alignItems: 'center' }}>
                        <p className="mb-0">Thank you for choosing {companyInfo.name}!</p>
                        <p className="mb-0">This is a computer-generated invoice.</p>
                    </div>


                </Card.Body>
            </Card>

            {/* PDF Preview Modal */}
            {showPreview && (
                <Modal show={showPDFPreview} onHide={() => setShowPDFPreview(false)} size="xl">
                    <Modal.Header closeButton>
                        <Modal.Title>PDF Preview - Invoice #{invoice.id}</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <div className="pdf-preview-container">
                            <div className="pdf-preview" style={{
                                border: '1px solid #ddd',
                                borderRadius: '8px',
                                padding: '20px',
                                backgroundColor: '#f8f9fa',
                                fontFamily: 'Arial, sans-serif',
                                maxHeight: '70vh',
                                overflow: 'auto',
                                display: 'flex',
                                justifyContent: 'center'
                            }}>
                                {/* PDF Preview Content - A4 Size */}
                                <div className="a4-preview">
                                    {/* Header */}
                                    <div className="invoice-header">
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <h2 style={{ margin: 0, fontSize: '20px' }}>{companyInfo.name}</h2>
                                            <h3 style={{ margin: 0, fontSize: '16px' }}>INVOICE</h3>
                                        </div>
                                    </div>

                                    {/* Invoice Info */}
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px' }}>
                                        <div>
                                            <p style={{ margin: '5px 0', fontSize: '12px' }}><strong>Invoice #:</strong> {invoice.id}</p>
                                            <p style={{ margin: '5px 0', fontSize: '12px' }}><strong>Date:</strong> {invoice.createdDate}</p>
                                            <p style={{ margin: '5px 0', fontSize: '12px' }}><strong>Due Date:</strong> {invoice.dueDate}</p>
                                        </div>
                                        <div>
                                            <span style={{
                                                backgroundColor: invoice.status === 'Paid' ? '#2ecc71' :
                                                    invoice.status === 'Pending' ? '#f1c40f' : '#e74c3c',
                                                color: 'white',
                                                padding: '4px 8px',
                                                borderRadius: '4px',
                                                fontSize: '10px',
                                                fontWeight: 'bold'
                                            }}>
                                                {invoice.status.toUpperCase()}
                                            </span>
                                        </div>
                                    </div>

                                    {/* Company and Client Info */}
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px' }}>
                                        <div style={{ flex: 1, marginRight: '20px' }}>
                                            <h4 style={{ color: '#34495e', marginBottom: '10px' }}>From:</h4>
                                            <p style={{ margin: '2px 0', fontSize: '12px' }}><strong>{companyInfo.name}</strong></p>
                                            <p style={{ margin: '2px 0', fontSize: '12px' }}>{companyInfo.address}</p>
                                            <p style={{ margin: '2px 0', fontSize: '12px' }}>{companyInfo.city}, {companyInfo.country}</p>
                                            <p style={{ margin: '2px 0', fontSize: '12px' }}>Phone: {companyInfo.phone}</p>
                                            <p style={{ margin: '2px 0', fontSize: '12px' }}>Email: {companyInfo.email}</p>
                                        </div>
                                        <div style={{ flex: 1 }}>
                                            <h4 style={{ color: '#34495e', marginBottom: '10px' }}>Bill To:</h4>
                                            <p style={{ margin: '2px 0', fontSize: '12px' }}><strong>{invoice.client.name}</strong></p>
                                            <p style={{ margin: '2px 0', fontSize: '12px' }}>{invoice.client.email}</p>
                                        </div>
                                    </div>

                                    {/* Service Table */}
                                    <div style={{ marginBottom: '20px' }}>
                                        <table className="invoice-table">
                                            <thead>
                                                <tr>
                                                    <th>Description</th>
                                                    <th>Package</th>
                                                    <th className="text-right">Amount</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr>
                                                    <td>{invoice.description}</td>
                                                    <td>{invoice.package}</td>
                                                    <td className="text-right">₹{(parseFloat(invoice.amount) || 0).toFixed(2)}</td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>

                                    {/* Total */}
                                    <div className="invoice-total">
                                        Total Amount: ₹{(parseFloat(invoice.amount) || 0).toFixed(2)}
                                    </div>

                                    {/* Payment Terms */}
                                    <div style={{ marginBottom: '20px' }}>
                                        <h4 style={{ color: '#34495e', marginBottom: '10px' }}>Payment Terms:</h4>
                                        <ul style={{ fontSize: '12px', margin: 0, paddingLeft: '20px' }}>
                                            <li>Payment due within 30 days of invoice date</li>
                                            <li>Late payments may incur additional charges</li>
                                            <li>For questions about this invoice, contact {companyInfo.email}</li>
                                        </ul>
                                    </div>

                                    {/* Footer */}
                                    <div className="invoice-footer">
                                        <p style={{ margin: '2px 0' }}>Thank you for choosing {companyInfo.name}!</p>
                                        <p style={{ margin: '2px 0' }}>This is a computer-generated invoice.</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowPDFPreview(false)}>
                            Close
                        </Button>
                        <Button variant="info" onClick={() => window.print()}>
                            <i className="fas fa-print me-2"></i>Print
                        </Button>
                        <Button variant="warning" onClick={() => {
                            handleDownload();
                            setShowPDFPreview(false);
                        }}>
                            <i className="fas fa-download me-2"></i>Download PDF
                        </Button>
                    </Modal.Footer>
                </Modal>
            )}
        </div>
    );
};

export default InvoiceTemplate;
